import { useSyncExternalStore } from 'react';
import { INSTALL_HINT_KEY, keysToForget, ownCaches } from './app-model.ts';
import { offlineReads } from '../offline-reads.ts';
import { outbox } from '../outbox.ts';

/**
 * The shell as an installed app, in the browser: the service worker's registration, the install
 * prompt the browser hands over, a new build waiting to take over, and what is forgotten when
 * somebody leaves. Only the workspace shell calls `startApp` (main.tsx) — the platform console's
 * address is never installable and never runs a worker.
 */

/** Chrome's install prompt. Not in the DOM's types, because no standard defines it. */
interface InstallPromptEvent extends Event { prompt(): Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> }

export interface AppState {
  /** The browser offered to install the app; `install()` opens its dialog. */
  canPrompt: boolean;
  /** A new build's worker is installed and waiting; `update()` lets it take over. */
  updateReady: boolean;
  /** The person said «Not now», or installed it, on this device. */
  dismissed: boolean;
}

let prompt: InstallPromptEvent | null = null;
let waiting: ServiceWorker | null = null;
let state: AppState = { canPrompt: false, updateReady: false, dismissed: readDismissed() };
const listeners = new Set<() => void>();

function set(next: Partial<AppState>) {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
}

function readDismissed(): boolean {
  try { return globalThis.localStorage?.getItem(INSTALL_HINT_KEY) === 'dismissed'; } catch { return false; }
}

export function useAppState(): AppState {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => state, () => state);
}

/** «Not now», or installed: never offered again on this device. */
export function dismissInstall(): void {
  try { localStorage.setItem(INSTALL_HINT_KEY, 'dismissed'); } catch { /* not offered again on this page */ }
  set({ dismissed: true });
}

export async function install(): Promise<void> {
  const p = prompt;
  if (!p) return;
  prompt = null;
  set({ canPrompt: false });
  await p.prompt();
  const choice = await p.userChoice.catch(() => ({ outcome: 'dismissed' as const }));
  if (choice.outcome === 'accepted') dismissInstall();
}

/** The waiting build takes over; the page reloads once it controls it (`controllerchange` below). */
export function update(): void {
  waiting?.postMessage({ type: 'SKIP_WAITING' });
}

export function startApp(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Kept for the hint's button instead of the browser's own mini-bar, which shows at a moment of
    // its choosing and in English.
    e.preventDefault();
    prompt = e as InstallPromptEvent;
    set({ canPrompt: true });
  });
  window.addEventListener('appinstalled', () => { prompt = null; dismissInstall(); set({ canPrompt: false }); });

  if (!('serviceWorker' in navigator)) return;
  const sw = navigator.serviceWorker;
  // A page that had a controller and gets a new one is running an old build under a new worker:
  // reload it, once, so the page and the worker are the same build.
  const hadController = !!sw.controller;
  let reloading = false;
  sw.addEventListener('controllerchange', () => {
    if (!hadController || reloading) return;
    reloading = true;
    window.location.reload();
  });
  // After the first paint: installing the worker fetches the offline page, and that must not compete
  // with the application's own first load.
  window.addEventListener('load', () => {
    void sw.register('/sw.js', { scope: '/' }).then((reg) => {
      const watch = (worker: ServiceWorker | null) => {
        if (!worker) return;
        const check = () => {
          // Waiting with a controller in place is a new build; the first install has nothing to replace.
          if (worker.state === 'installed' && sw.controller) { waiting = worker; set({ updateReady: true }); }
        };
        check();
        worker.addEventListener('statechange', check);
      };
      watch(reg.waiting);
      reg.addEventListener('updatefound', () => watch(reg.installing));
      // A phone keeps an installed app open for days: ask for a new build when it comes back.
      document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void reg.update().catch(() => {}); });
    }).catch(() => { /* no worker: the app still runs, it just does not open offline */ });
  });
}

/**
 * Somebody signed out or switched workspace: this device forgets them. The worker's caches hold the
 * shell's own files and nothing of anybody's, but they go too, and the worker fetches the offline
 * page again; `sessionStorage` goes whole; `localStorage` keeps only how the app is shown
 * (`keysToForget`); the answers kept to be read offline (`source.offline_read`) go whole — every
 * person's (offline-reads.ts) — and so does the outbox, every request still waiting to be sent
 * (outbox.ts, `core-outbox`): a shared phone never sends one person's queue as the next person.
 * Bounded, so a worker that never answers cannot hold a sign-out up.
 */
export async function forgetDevice(): Promise<void> {
  try { globalThis.sessionStorage?.clear(); } catch { /* nothing to clear */ }
  const reads = offlineReads.forget();
  const queued = outbox.forget();
  try {
    const store = globalThis.localStorage;
    if (store) {
      const keys = Array.from({ length: store.length }, (_, i) => store.key(i)).filter((k): k is string => k !== null);
      for (const k of keysToForget(keys)) store.removeItem(k);
    }
  } catch { /* no storage: nothing was kept */ }
  const caches = globalThis.caches;
  const work = (async () => {
    if (caches) await Promise.all(ownCaches(await caches.keys()).map((k) => caches.delete(k)));
    const worker = globalThis.navigator?.serviceWorker?.controller;
    if (worker) {
      await new Promise<void>((resolve) => {
        const channel = new MessageChannel();
        channel.port1.onmessage = () => resolve();
        worker.postMessage({ type: 'FORGET' }, [channel.port2]);
      });
    }
  })().catch(() => {});
  await Promise.race([Promise.all([work, reads, queued]), new Promise((r) => setTimeout(r, 1500))]);
}
